import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Project } from '../types';
import { FaCoins, FaHandshake, FaVoteYea, FaGlobe, FaCube, FaRocket } from 'react-icons/fa';
import { 
  SiSolana, SiReact, SiNodedotjs, SiTypescript, SiSolidity, SiNextdotjs, SiIpfs, SiPolygon, SiVuedotjs, SiThealgorithms, 
  SiEthereum
} from 'react-icons/si';

const Projects: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const projects: Project[] = [
    {
      id: '1',
      title: 'DeBot.ai',
      description: 'Advanced AI-powered multi-chain platform for discovering and sniping meme tokens across Solana, BSC, Base, Ethereum, xLayer, Monad, and Juchain. Features smart money tracking, real-time token analysis, and automated sniping tools to follow winning strategies and maximize trading efficiency.',
      image: './images/debot-ai.jpg',
      technologies: ['Multi-chain', 'AI/ML', 'React', 'TypeScript', 'Trading APIs'],
      githubUrl: 'https://github.com',
      liveUrl: 'https://debot.ai',
      category: 'defi'
    },
    {
      id: '2',
      title: 'Radiant Trading Bot Platform',
      description: 'Trading bot service for NFT holders featuring advanced automation. Includes sniper bot, copy bot, volume bot, and bundler bot for maximizing trading efficiency on Solana.',
      image: './images/radiant.jpg',
      technologies: ['Solana', 'React', 'Node.js', 'TypeScript', 'Bot APIs'],
      githubUrl: 'https://github.com/hodlwarden/radiant-frontend',
      liveUrl: 'https://www.radiantdapp.xyz/',
      category: 'defi'
    },
    {
      id: '3',
      title: 'ChainSwap Multi-Chain DEX',
      description: 'Led Solana development as Lead Developer, implementing Chainlink CCIP for cross-chain interoperability. Built a multi-chain decentralized exchange with advanced trading features and secure cross-chain asset transfers.',
      image: './images/chainswap.webp',
      technologies: ['Solana', 'Rust', 'Chainlink CCIP', 'React', 'Cross-chain'],
      githubUrl: 'https://github.com',
      liveUrl: 'https://exchange.chainswap.com',
      category: 'defi'
    },
    {
      id: '4',
      title: 'Whaleden',
      description: 'Developed secure and efficient Solana smart contracts for the Whaleden project. Delivered well-documented, production-ready smart contract solutions with comprehensive testing and security audits.',
      image: './images/whaleden.webp',
      technologies: ['Solana', 'Rust', 'Anchor', 'Web3.js'],
      githubUrl: 'https://github.com',
      liveUrl: 'https://whaleden.com',
      category: 'defi'
    },
    {
      id: '5',
      title: 'Moduluc Smart Contract Platform',
      description: 'Spearheaded implementation of comprehensive testing frameworks and CI/CD pipelines for smart contract development. Enhanced code reliability and maintainability through best practices and automated testing solutions.',
      image: './images/moduluc.webp',
      technologies: ['Solidity', 'Hardhat', 'TypeScript', 'CI/CD', 'Testing'],
      githubUrl: 'https://github.com',
      liveUrl: 'https://moduluc.com',
      category: 'web3'
    },
    {
      id: '6',
      title: 'BlazeSwap DEX',
      description: 'Cross-chain DEX aggregator built on XION blockchain with Solana integration. Features lightning-fast swaps without bridges, full control via Meta Accounts, and liquidity farming with lifetime reward multipliers.',
      image: './images/blazeswap.webp',
      technologies: ['Solana', 'Rust', 'React', 'TypeScript', 'AMM'],
      githubUrl: 'https://github.com',
      liveUrl: 'https://blazeswap.io',
      category: 'defi'
    },
    {
      id: '7',
      title: 'Atlas3 NFT Launchpad',
      description: 'Comprehensive NFT platform by Blocksmith Labs for discovering, curating, and managing NFT projects. Features giveaways, allowlist management, and cross-chain compatibility with Enjin Blockchain integration.',
      image: './images/atlas3.webp',
      technologies: ['Solana', 'Next.js', 'Rust', 'IPFS', 'Web3'],
      githubUrl: 'https://github.com',
      liveUrl: 'https://atlas3.io',
      category: 'nft'
    },
    {
      id: '8',
      title: 'Meegos NFT Collection',
      description: 'Collection of 10,000 uniquely designed characters by Blocksmith Labs. Features MeeArcade gaming platform, MeeStream content creation, and Welcome App social hub for community engagement and holder rewards.',
      image: './images/meegos.webp',
      technologies: ['Solana', 'Rust', 'React', 'Metaplex', 'Web3.js'],
      githubUrl: 'https://github.com',
      liveUrl: 'https://meegos.io',
      category: 'nft'
    },
    {
      id: '9',
      title: 'Saint Protocol',
      description: 'DeFi protocol with innovative yield farming and liquidity mining mechanisms. Implements advanced tokenomics and governance features for decentralized community management.',
      image: './images/saint.webp',
      technologies: ['Solana', 'Rust', 'React', 'DeFi', 'Governance'],
      githubUrl: 'https://github.com',
      liveUrl: '#',
      category: 'defi'
    },
    {
      id: '10',
      title: 'Tizz Finance',
      description: 'Next-generation DeFi platform offering lending, borrowing, and yield optimization strategies. Features automated vault strategies and risk management tools for maximizing returns.',
      image: './images/tizzfinanace.webp',
      technologies: ['Solana', 'Rust', 'TypeScript', 'DeFi', 'Vaults'],
      githubUrl: 'https://github.com',
      liveUrl: '#',
      category: 'defi'
    },
    {
      id: '11',
      title: 'Dramsky Social Platform',
      description: 'Web3 social platform integrating blockchain technology for content monetization and community governance. Features token-gated content and decentralized identity verification.',
      image: './images/dramsky.webp',
      technologies: ['Solana', 'React', 'Node.js', 'IPFS', 'Social'],
      githubUrl: 'https://github.com',
      liveUrl: '#',
      category: 'web3'
    },
    {
      id: '12',
      title: 'Hype.fun Social Meme Platform',
      description: 'Solana-based social meme platform where users can create and trade "attention tokens" linked to social media profiles. Revolutionary P2E mechanics combined with social trading.',
      image: './images/hpe.jpg',
      technologies: ['Solana', 'Vue.js', 'The Graph', 'IPFS', 'Web3'],
      githubUrl: 'https://github.com/hodlwarden/hype-fun-frontend',
      liveUrl: 'https://hype.fun',
      category: 'meme'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const projectVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'defi': return <FaCoins style={{ color: '#ffffff', fontSize: '3rem' }} />;
      case 'nft': return <FaHandshake style={{ color: '#ffffff', fontSize: '3rem' }} />;
      case 'dao': return <FaVoteYea style={{ color: '#ffffff', fontSize: '3rem' }} />;
      case 'web3': return <FaGlobe style={{ color: '#ffffff', fontSize: '3rem' }} />;
      case 'meme': return <FaRocket style={{ color: '#ffffff', fontSize: '3rem' }} />;
      default: return <FaCube style={{ color: '#ffffff', fontSize: '3rem' }} />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'defi': return '#00d4ff';
      case 'nft': return '#ffd700';
      case 'dao': return '#8a2be2';
      case 'web3': return '#00ff88';
      case 'meme': return '#ff69b4';
      default: return '#ffffff';
    }
  };

  const techIconFor = (tech: string) => {
    const normalized = tech.toLowerCase();
    if (normalized.includes('solana')) return <SiSolana />;
    if (normalized.includes('react')) return <SiReact />;
    if (normalized.includes('node')) return <SiNodedotjs />;
    if (normalized.includes('type')) return <SiTypescript />;
    if (normalized.includes('solidity')) return <SiSolidity />;
    if (normalized.includes('next')) return <SiNextdotjs />;
    if (normalized.includes('ipfs')) return <SiIpfs />;
    if (normalized.includes('polygon')) return <SiPolygon />;
    if (normalized.includes('ethereum')) return <SiEthereum />;
    if (normalized.includes('vue')) return <SiVuedotjs />;
    if (normalized.includes('graph')) return <SiThealgorithms />; // closest available icon
    return null;
  };

  return (
    <section id="projects" className="projects" ref={ref}>
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
        >
          Featured Projects
        </motion.h2>

        <motion.div
          className="projects-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {projects.map((project, _index) => (
            <motion.a
              key={project.id}
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="project-card"
              variants={projectVariants}
              whileHover={{ 
                scale: 1.02,
                y: -10,
                boxShadow: `0 20px 40px ${getCategoryColor(project.category)}20`
              }}
              style={{ 
                '--project-color': getCategoryColor(project.category),
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer'
              } as React.CSSProperties}
            >
              <motion.div 
                className="project-image"
                style={{ background: `linear-gradient(135deg, ${getCategoryColor(project.category)}, ${getCategoryColor(project.category)}80)` }}
                whileHover={{ scale: 1.05 }}
              >
                {project.image ? (
                  <img src={project.image} alt={project.title} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} />
                ) : (
                  <div className="project-placeholder">
                    {getCategoryIcon(project.category)}
                  </div>
                )}
                <motion.div
                  className="project-category"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                >
                  {project.category.toUpperCase()}
                </motion.div>
              </motion.div>

              <div className="project-content">
                <h3>{project.title}</h3>
                <p>{project.description}</p>

                <motion.div 
                  className="project-tech"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {project.technologies.map((tech, techIndex) => (
                    <motion.span
                      key={techIndex}
                      className="tech-tag"
                      style={{ 
                        backgroundColor: `${getCategoryColor(project.category)}20`,
                        color: getCategoryColor(project.category)
                      }}
                      whileHover={{ scale: 1.1 }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {techIconFor(tech)}
                        <span>{tech}</span>
                      </span>
                    </motion.span>
                  ))}
                </motion.div>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;

